import PageBackTitle from "../Component/PageBackTitle";
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./User.css";
import "./Button.css";
import "../forms/UserForm.css";
import "../BarrelManagement/PurchaseIndents/purchaseIndents.css";
import "../SafetyObservation/safetyObservation.css";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import Select from "react-select";
import { Link, useNavigate } from "react-router-dom";
import { HiOutlinePlus } from "react-icons/hi";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import { MdExpandMore, MdOutlineModeEditOutline } from "react-icons/md";
import API_URL from "../../src/Config";
import Pagination from "../Component/Pagination";
import {
  fetchDepartmentOptions,
  fetchUserOptions,
  formatDateDisplay,
  getObservationSeverityLabel,
  getObservationStatusLabel,
  getObservationTypeLabel,
  getStatusColor,
  OBSERVATION_SEVERITY_OPTIONS,
  OBSERVATION_STATUS_OPTIONS,
  OBSERVATION_TYPE_OPTIONS,
  parseApiError,
} from "../SafetyObservation/observationUtils";

const INITIAL_FILTERS = {
  status: "",
  type: "",
  severity: "",
  department_id: "",
  start_date: "",
  end_date: "",
};

const INITIAL_ACTION_FORM = {
  description: "",
  assigned_to: "",
  due_date: "",
};

function SafetyObservationList({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [observations, setObservations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [paginationKey, setPaginationKey] = useState(0);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState(null);
  const [observationToDelete, setObservationToDelete] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [assignForm, setAssignForm] = useState(INITIAL_ACTION_FORM);
  const [assignErrors, setAssignErrors] = useState({});
  const [assigning, setAssigning] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const popupRef = useRef(null);
  const hasActiveFilters = Boolean(
    searchInput.trim() ||
    filters.status.trim() ||
      filters.type.trim() ||
      filters.severity.trim() ||
      filters.department_id.trim() ||
      filters.start_date.trim() ||
      filters.end_date.trim(),
  );

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const selectedStatusOption = useMemo(
    () =>
      OBSERVATION_STATUS_OPTIONS.find(
        (option) => option.value === filters.status,
      ) || null,
    [filters.status],
  );

  const selectedTypeOption = useMemo(
    () =>
      OBSERVATION_TYPE_OPTIONS.find(
        (option) => option.value === filters.type,
      ) || null,
    [filters.type],
  );

  const selectedSeverityOption = useMemo(
    () =>
      OBSERVATION_SEVERITY_OPTIONS.find(
        (option) => option.value === filters.severity,
      ) || null,
    [filters.severity],
  );

  const selectedDepartmentOption = useMemo(
    () =>
      departments.find((option) => option.value === filters.department_id) ||
      null,
    [departments, filters.department_id],
  );

  const selectedAssignee = useMemo(
    () =>
      users.find((option) => option.value === assignForm.assigned_to) || null,
    [users, assignForm.assigned_to],
  );

  const fetchObservations = async (
    currentPage,
    currentItemsPerPage,
    activeFilters,
  ) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      const params = {
        page: currentPage,
        per_page: currentItemsPerPage,
      };

      if (activeFilters.status) params.status = activeFilters.status;
      if (activeFilters.type) params.type = activeFilters.type;
      if (activeFilters.severity) params.severity = activeFilters.severity;
      if (activeFilters.department_id)
        params.department_id = activeFilters.department_id;
      if (activeFilters.start_date) {
        params.start_date = activeFilters.start_date;
      }
      if (activeFilters.end_date) {
        params.end_date = activeFilters.end_date;
      }
      if (searchInput.trim()) {
        params.search = searchInput.trim();
      }

      const response = await axios.get(`${API_URL}/api/safety/observations`, {
        headers: {
          Authorization: authKey,
        },
        params,
      });

      setObservations(response.data.items || []);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      showSnackbar(
        parseApiError(error, "Failed to fetch observations"),
        "error",
      );
      setObservations([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const loadPageData = async () => {
    try {
      const [departmentOptions, userOptions] = await Promise.all([
        fetchDepartmentOptions(),
        fetchUserOptions(),
      ]);

      setDepartments(departmentOptions);
      setUsers(userOptions);
    } catch (error) {
      showSnackbar(
        parseApiError(error, "Failed to load filter options"),
        "error",
      );
    }
  };

  useEffect(() => {
    loadPageData();
  }, []);

  useEffect(() => {
    fetchObservations(page, itemsPerPage, filters);
  }, [page, itemsPerPage, filters, searchInput]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setSelectedRowIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePaginationChange = (currentPage, nextItemsPerPage) => {
    setPage(currentPage);
    setItemsPerPage(nextItemsPerPage);
  };

  const handleRefresh = () => {
    if (!hasActiveFilters) {
      fetchObservations(page, itemsPerPage, filters);
      return;
    }

    setFilters(INITIAL_FILTERS);
    setSearchInput("");
    setPage(1);
    setPaginationKey((prevState) => prevState + 1);
  };

  const handleFilterChange = (fieldName, value) => {
    setFilters((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }));
    setPage(1);
    setPaginationKey((prevState) => prevState + 1);
  };

  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
    setPage(1);
    setPaginationKey((prevState) => prevState + 1);
  };

  const handleButtonClick = (index, event) => {
    event.stopPropagation();
    setSelectedRowIndex((prevState) => (prevState === index ? null : index));
  };

  const handleOpenAssignModal = (observation) => {
    setSelectedObservation(observation);
    setAssignForm(INITIAL_ACTION_FORM);
    setAssignErrors({});
    setAssignModalOpen(true);
  };

  const handleCloseAssignModal = () => {
    if (assigning) return;
    setAssignModalOpen(false);
    setSelectedObservation(null);
    setAssignForm(INITIAL_ACTION_FORM);
    setAssignErrors({});
  };

  const validateAssignForm = () => {
    const validationErrors = {};

    if (!assignForm.description.trim()) {
      validationErrors.description = "Description is required.";
    }

    if (!assignForm.assigned_to) {
      validationErrors.assigned_to = "Assignee is required.";
    }

    return validationErrors;
  };

  const handleAssignSubmit = async () => {
    const validationErrors = validateAssignForm();
    if (Object.keys(validationErrors).length > 0) {
      setAssignErrors(validationErrors);
      return;
    }

    try {
      setAssigning(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.post(
        `${API_URL}/api/safety/observations/${selectedObservation.id}/actions`,
        {
          description: assignForm.description.trim(),
          assigned_to: assignForm.assigned_to,
          due_date: assignForm.due_date || null,
        },
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "application/json",
          },
        },
      );

      showSnackbar(response.data?.message || "Assignee added successfully");
      handleCloseAssignModal();
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to assign action"), "error");
    } finally {
      setAssigning(false);
    }
  };

  const handleDeleteObservation = async () => {
    if (!observationToDelete) return;

    try {
      setIsDeleting(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.delete(
        `${API_URL}/api/safety/observations/${observationToDelete.id}`,
        {
          headers: {
            Authorization: authKey,
          },
        },
      );

      showSnackbar(
        response.data?.message || "Observation deleted successfully",
      );
      const nextPage = observations.length === 1 && page > 1 ? page - 1 : page;
      setObservationToDelete(null);

      if (nextPage !== page) {
        setPage(nextPage);
      } else {
        fetchObservations(nextPage, itemsPerPage, filters);
      }
    } catch (error) {
      showSnackbar(
        parseApiError(error, "Failed to delete observation"),
        "error",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>Observation List</PageBackTitle>
              <div className="data-list-heading-content-right-side">
                <div className="search-bar-container">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    className="search-bar"
                  />
                  <IoSearchOutline className="search-bar-icon" />
                </div>

                <Link to="/safety-observations-form" className="data-list-new">
                  <Tooltip title="New Observation" arrow>
                    <button className="new-button">
                      <HiOutlinePlus className="new-plus-icon" /> New
                    </button>
                  </Tooltip>
                </Link>

                <Tooltip title="Refresh List" arrow>
                  <button className="refresh-button" onClick={handleRefresh}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>

          <div className="header-separator"></div>

          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div
              className="filter-card observation-filter-card"
              style={{
                width: "100%",
                background: "#fff",
                border: "1px solid #e0e0e0",
                padding: "12px 20px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "20px",
              }}
            >
              <div className="observation-dropdown-field">
                <Select
                  options={OBSERVATION_STATUS_OPTIONS}
                  value={selectedStatusOption}
                  className="select-input"
                  onChange={(selectedOption) =>
                    handleFilterChange(
                      "status",
                      selectedOption ? selectedOption.value : "",
                    )
                  }
                  placeholder="All Status"
                  isClearable
                />
              </div>

              <div className="observation-dropdown-field">
                <Select
                  options={OBSERVATION_TYPE_OPTIONS}
                  value={selectedTypeOption}
                  className="select-input"
                  onChange={(selectedOption) =>
                    handleFilterChange(
                      "type",
                      selectedOption ? selectedOption.value : "",
                    )
                  }
                  placeholder="All Type"
                  isClearable
                />
              </div>

              <div className="observation-dropdown-field">
                <Select
                  options={OBSERVATION_SEVERITY_OPTIONS}
                  value={selectedSeverityOption}
                  className="select-input"
                  onChange={(selectedOption) =>
                    handleFilterChange(
                      "severity",
                      selectedOption ? selectedOption.value : "",
                    )
                  }
                  placeholder="All Severity"
                  isClearable
                />
              </div>

              <div className="observation-dropdown-field">
                <Select
                  options={departments}
                  value={selectedDepartmentOption}
                  className="select-input"
                  onChange={(selectedOption) =>
                    handleFilterChange(
                      "department_id",
                      selectedOption ? selectedOption.value : "",
                    )
                  }
                  placeholder="All Department"
                  isClearable
                  isSearchable
                />
              </div>

              <div className="observation-filter-date">
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(event) =>
                    handleFilterChange("start_date", event.target.value)
                  }
                />
              </div>

              <div className="observation-filter-date">
                <input
                  type="date"
                  value={filters.end_date}
                  min={filters.start_date || undefined}
                  onChange={(event) =>
                    handleFilterChange("end_date", event.target.value)
                  }
                />
              </div>
            </div>
          </div>

          <div className="data-list-body">
            <div className="data-list-scroll-body-purchase_indent">
              <div className="data-list-tables">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : observations.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th className="center-alignment">SR. NO.</th>
                        <th>TITLE</th>
                        <th>TYPE</th>
                        <th>SEVERITY</th>
                        <th>DEPARTMENT</th>
                        {/* <th>LOCATION</th> */}
                        <th>OBSERVED DATE</th>
                        <th>OBSERVED BY</th>
                        <th className="center-alignment">STATUS</th>
                        <th className="center-alignment">ACTION</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {observations.map((observation, index) => (
                        <tr key={observation.id} className="hoverable-row">
                          <td className="center-alignment">
                            {(page - 1) * itemsPerPage + index + 1}
                          </td>
                          <td>{observation.title || "NA"}</td>
                          <td>{getObservationTypeLabel(observation.type)}</td>
                          <td>
                            {getObservationSeverityLabel(observation.severity)}
                          </td>
                          <td>{observation.department_name || "NA"}</td>
                          {/* <td>{observation.location || "NA"}</td> */}
                          <td>
                            {formatDateDisplay(observation.observed_date)}
                          </td>
                          <td>{observation.observer_name || "NA"}</td>
                          <td className="status-schedules">
                            <div
                              className="observation-status-pill"
                              style={{
                                backgroundColor: getStatusColor(
                                  observation.status,
                                ),
                              }}
                            >
                              {getObservationStatusLabel(observation.status)}
                            </div>
                          </td>
                          <td className="center-alignment">
                            <div className="observation-action-buttons">
                              <button
                                className="button-bg"
                                onClick={() =>
                                  navigate(
                                    `/safety-observations-view/${observation.id}`,
                                  )
                                }
                              >
                                View
                              </button>
                              {observation.status !== "closed" && (
                                <button
                                  className="button-bg"
                                  onClick={() =>
                                    handleOpenAssignModal(observation)
                                  }
                                >
                                  Assign
                                </button>
                              )}
                              <button
                                className="edit-data-button"
                                onClick={(event) =>
                                  handleButtonClick(index, event)
                                }
                              >
                                <div className="edit-button">
                                  <MdExpandMore />
                                </div>
                              </button>
                              {/* <button
                                className="button-bg"
                                onClick={() => setObservationToDelete(observation)}
                              >
                                Delete
                              </button> */}
                            </div>
                            {selectedRowIndex === index && (
                              <div
                                className="edit-popup"
                                style={{ top: "58%", right: "4%" }}
                                ref={popupRef}
                              >
                                <div className="edit-popup-contant">
                                  <div className="edit-popup-button">
                                    <Link
                                      to={`/edit-safety-observation/${observation.id}`}
                                      className="inquiry-new"
                                    >
                                      <button className="edit-button-inquiry">
                                        <MdOutlineModeEditOutline /> Edit
                                      </button>
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">
                      No Observations Found
                    </div>
                  </div>
                )}
              </div>

              {totalItems > 0 && (
                <Pagination
                  key={paginationKey}
                  showPerPage={itemsPerPage}
                  total={totalItems}
                  onPaginationChange={handlePaginationChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {assignModalOpen && selectedObservation && (
        <div
          className="observation-assign-backdrop"
          onClick={handleCloseAssignModal}
        >
          <div
            className="observation-assign-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="observation-assign-header">
              <div className="observation-assign-title">
                Assign Action - {selectedObservation.title}
              </div>
              {/* <button
                type="button"
                className="view-close-button"
                onClick={handleCloseAssignModal}
              >
                x
              </button> */}
            </div>

            <div className="observation-assign-body">
              <div className="observation-assign-field">
                <label>Due Date</label>
                <input
                  type="date"
                  value={assignForm.due_date}
                  onChange={(event) =>
                    setAssignForm((prevState) => ({
                      ...prevState,
                      due_date: event.target.value,
                    }))
                  }
                />
              </div>

              <div className="observation-assign-field">
                <label>Assignee</label>
                <Select
                  options={users}
                  value={selectedAssignee}
                  className="select-input"
                  onChange={(selectedOption) => {
                    setAssignForm((prevState) => ({
                      ...prevState,
                      assigned_to: selectedOption ? selectedOption.value : "",
                    }));
                    setAssignErrors((prevState) => ({
                      ...prevState,
                      assigned_to: "",
                    }));
                  }}
                  placeholder="Select assignee"
                  isClearable
                  isSearchable
                />
                {assignErrors.assigned_to && (
                  <div className="observation-inline-error">
                    {assignErrors.assigned_to}
                  </div>
                )}
              </div>

              <div className="observation-assign-field">
                <label>Action Description</label>
                <textarea
                  rows={4}
                  value={assignForm.description}
                  onChange={(event) => {
                    setAssignForm((prevState) => ({
                      ...prevState,
                      description: event.target.value,
                    }));
                    setAssignErrors((prevState) => ({
                      ...prevState,
                      description: "",
                    }));
                  }}
                  placeholder="Enter action description"
                />
                {assignErrors.description && (
                  <div className="observation-inline-error">
                    {assignErrors.description}
                  </div>
                )}
              </div>
            </div>

            <div className="observation-assign-footer">
              <button
                type="button"
                className="data-form-cancel-button"
                onClick={handleCloseAssignModal}
                disabled={assigning}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button-bg"
                onClick={handleAssignSubmit}
                disabled={assigning}
              >
                {assigning ? "Assigning..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {observationToDelete && (
        <div className="confirm-delete-popup">
          <div className="confirm-delete-popup-content">
            <div className="confirm-model">
              <div className="confirm-model-content">
                <div className="confirm-model-content-body">
                  <p>
                    Are you sure you want to delete observation{" "}
                    <strong>{observationToDelete.title || "this item"}</strong>?
                  </p>
                </div>
                <div className="confirm-model-content-footer">
                  <button
                    className="confirm-delete-button"
                    onClick={handleDeleteObservation}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Yes"}
                  </button>
                  <button
                    className="confirm-cancel-button"
                    onClick={() => !isDeleting && setObservationToDelete(null)}
                    disabled={isDeleting}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default SafetyObservationList;
